import api from './api';

const API_BASE = String(import.meta.env.VITE_API_URL || '/meos/api').replace(/\/$/, '');

const getJwtToken = (): string => {
  try {
    return String(localStorage.getItem('ma_jwt') || '').trim();
  } catch {
    return '';
  }
};

const withApiBase = (value?: string | null): string | null => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const resolved = raw.startsWith('/api/')
    ? `${API_BASE.replace(/\/api$/, '')}${raw}`
    : `${API_BASE}${raw.startsWith('/') ? '' : '/'}${raw}`;
  if (!/\/api\/whatsapp\/media\//.test(resolved)) return resolved;
  const token = getJwtToken();
  if (!token) return resolved;
  return `${resolved}${resolved.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
};

const freshConfig = (params?: Record<string, unknown>) => ({
  params: {
    ...(params || {}),
    _ts: Date.now(),
  },
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
});

const WHATSAPP_QUEUE_GLOBAL_DEDUPE_MS = 3000;
const whatsAppQueueBroker = new Map<string, { at: number; promise: Promise<any> }>();
const WHATSAPP_THREAD_DETAIL_GLOBAL_DEDUPE_MS = 2500;
const whatsAppThreadDetailBroker = new Map<string, { at: number; promise: Promise<any> }>();

const isWhatsAppDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return String(window.localStorage.getItem('MAOS_DEBUG_WA') || '').trim() === '1';
  } catch {
    return false;
  }
};

const normalizeQueueBrokerKey = (params?: Record<string, unknown>) =>
  JSON.stringify(
    Object.entries(params || {})
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .sort(([a], [b]) => a.localeCompare(b)),
  );

const normalizeThreadDetailBrokerKey = (
  threadId: string,
  params?: { messageLimit?: number; beforeSequenceNumber?: number | null },
) =>
  JSON.stringify({
    threadId: String(threadId || '').trim(),
    messageLimit:
      Number.isFinite(Number(params?.messageLimit)) && Number(params?.messageLimit) > 0
        ? Math.round(Number(params?.messageLimit))
        : undefined,
    beforeSequenceNumber:
      Number.isFinite(Number(params?.beforeSequenceNumber))
        ? Number(params?.beforeSequenceNumber)
        : undefined,
  });

export type WhatsAppThreadStatus = 'OPEN' | 'WAITING_CLIENT' | 'RESOLVED_WON' | 'RESOLVED_LOST';

export type WhatsAppQueueItem = {
  id: string;
  conversationId?: string;
  numberId: string;
  numberLabel?: string | null;
  numberPhone?: string | null;
  ownerUserId?: string | null;
  ownerName?: string | null;
  customerId?: string | null;
  maosCustomerName?: string | null;
  customerJid: string;
  displayName?: string | null;
  displayPhone?: string | null;
  identityKind?: string | null;
  participantMap?: Record<string, {
    jid?: string | null;
    phone?: string | null;
    displayName?: string | null;
    displayPhone?: string | null;
    kind?: string | null;
  }> | null;
  chatType?: 'DIRECT' | 'GROUP' | string | null;
  groupJid?: string | null;
  groupName?: string | null;
  customerPhone?: string | null;
  customerName?: string | null;
  profilePhotoUrl?: string | null;
  salespersonUserId?: string | null;
  salespersonName?: string | null;
  assignedUserId?: string | null;
  assignedUserName?: string | null;
  status: WhatsAppThreadStatus;
  lastInboundAt?: string | null;
  lastOutboundAt?: string | null;
  lastMessageAt?: string | null;
  slaDeadlineAt?: string | null;
  needsReply: boolean;
  isOverdue: boolean;
  escalationLevel: number;
  unreadInboundCount?: number;
  lastReadAt?: string | null;
  lastReadMessageId?: string | null;
  lastMessage?: {
    id: string;
    externalMessageId?: string | null;
    direction: 'INBOUND' | 'OUTBOUND';
    body?: string | null;
    resolvedBody?: string | null;
    displayBody?: string | null;
    messageType?: string | null;
    occurredAt?: string | null;
    sequenceNumber?: number | null;
    createdAt?: string | null;
  } | null;
  _preservedFromRail?: boolean;
  _outOfCurrentFilter?: boolean;
  _preservedReason?: 'OPENED' | 'RECENT_ACTIVE' | null;
};

export type WhatsAppQueueResponse = {
  items: WhatsAppQueueItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  unreadInboundTotal?: number;
};

export type WhatsAppOwnerStat = {
  userId: string | null;
  name: string;
  active: number;
  needsReply: number;
  overdue: number;
  resolvedWon7d: number;
  resolvedLost7d: number;
  resolvedWon30d: number;
  resolvedLost30d: number;
};

export type WhatsAppStatsReport = {
  totals: {
    active: number;
    open: number;
    waitingClient: number;
    needsReply: number;
    overdue: number;
    resolvedWon7d: number;
    resolvedLost7d: number;
    resolvedWon30d: number;
    resolvedLost30d: number;
  };
  byOwner: WhatsAppOwnerStat[];
  generatedAt: string;
};

export type WhatsAppThreadDetail = {
  id: string;
  conversationId?: string;
  numberId: string;
  customerId?: string | null;
  customerJid: string;
  displayName?: string | null;
  displayPhone?: string | null;
  identityKind?: string | null;
  customerPhone?: string | null;
  customerName?: string | null;
  maosCustomerName?: string | null;
  Customer?: { id?: string; name?: string; email?: string | null; phoneNumber?: string | null } | null;
  profilePhotoUrl?: string | null;
  groupParticipants?: Array<{
    jid: string;
    phone?: string | null;
    name?: string | null;
    admin?: boolean;
    superadmin?: boolean;
    profilePhotoUrl?: string | null;
    profilePhotoFetchedAt?: string | null;
  }> | null;
  participantMap?: Record<string, {
    jid?: string | null;
    phone?: string | null;
    displayName?: string | null;
    displayPhone?: string | null;
    kind?: string | null;
  }> | null;
  salespersonUserId?: string | null;
  Salesperson?: {
    id?: string;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  assignedUserId?: string | null;
  Number?: {
    ownerUserId?: string | null;
    Owner?: {
      id?: string;
      email?: string | null;
      firstName?: string | null;
      lastName?: string | null;
    } | null;
  } | null;
  Assignee?: {
    id?: string;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  status: WhatsAppThreadStatus;
  lastInboundAt?: string | null;
  lastOutboundAt?: string | null;
  lastMessageAt?: string | null;
  slaDeadlineAt?: string | null;
  lastReadAt?: string | null;
  lastReadMessageId?: string | null;
  needsReply: boolean;
  isOverdue: boolean;
  escalationLevel: number;
  Messages?: Array<{
    id: string;
    externalMessageId?: string | null;
    direction: 'INBOUND' | 'OUTBOUND';
    body?: string | null;
    messageType?: string | null;
    searchText?: string | null;
    senderName?: string | null;
    senderJid?: string | null;
    isGroupMessage?: boolean;
    groupParticipantJid?: string | null;
    mediaUrl?: string | null;
    mediaMimeType?: string | null;
    mediaFileName?: string | null;
    mediaFileSizeBytes?: number | null;
    rawPayload?: any;
    occurredAt?: string | null;
    sequenceNumber?: number | null;
    MediaAsset?: {
      id: string;
      kind: 'IMAGE' | 'DOCUMENT' | 'AUDIO' | 'VIDEO';
      storageKey: string;
      mimeType?: string | null;
      fileName?: string | null;
      fileSizeBytes?: number | null;
      durationSeconds?: number | null;
      extractedText?: string | null;
      transcriptText?: string | null;
      transcriptStatus?: 'NOT_REQUESTED' | 'PENDING' | 'COMPLETED' | 'FAILED';
      transcriptGeneratedAt?: string | null;
      searchText?: string | null;
      evidenceType?: string | null;
      caption?: string | null;
      aiSummary?: string | null;
      detectedEntities?: any;
      linkedRequestIds?: any;
    } | null;
    createdAt?: string | null;
    sentAt?: string | null;
    receivedAt?: string | null;
  }>;
  Requests?: Array<{
    id: string;
    status?: string | null;
    requestType?: string | null;
    title?: string | null;
    summary?: string | null;
    routeFrom?: string | null;
    routeTo?: string | null;
    travelDateText?: string | null;
    travelDate?: string | null;
    passengerCount?: number | null;
    missingFields?: string[] | null;
    extractedEntities?: any;
    sourceMessageId?: string | null;
    latestMessageId?: string | null;
    metadata?: any;
    createdAt?: string | null;
    updatedAt?: string | null;
    EvidenceLinks?: Array<{
      id: string;
      MediaAsset?: {
        id: string;
        evidenceType?: string | null;
        fileName?: string | null;
        mimeType?: string | null;
        caption?: string | null;
        aiSummary?: string | null;
        transcriptText?: string | null;
        extractedText?: string | null;
      } | null;
    }>;
  }>;
  Alerts?: Array<{
    id: string;
    alertType: 'NEEDS_REPLY' | 'OVERDUE' | 'ESCALATED' | 'SESSION_OFFLINE';
    sentAt?: string | null;
    payload?: {
      eventType?: 'STATUS_CHANGE' | 'BULK_ASSIGN' | 'BULK_WAITING_CLIENT' | 'TRANSFER';
      actorUserId?: string | null;
      actorLabel?: string | null;
      fromStatus?: WhatsAppThreadStatus;
      toStatus?: WhatsAppThreadStatus;
      assignedUserId?: string | null;
      assignedUserLabel?: string | null;
      fromAssignedUserId?: string | null;
      toAssignedUserId?: string | null;
      note?: string | null;
      threadCount?: number | null;
      status?: WhatsAppThreadStatus;
    } | null;
  }>;
  Notes?: WhatsAppThreadNote[];
  messagePage?: {
    limit: number;
    beforeSequenceNumber?: number | null;
    hasMoreMessagesBefore: boolean;
    nextBeforeSequenceNumber?: number | null;
    oldestLoadedSequenceNumber?: number | null;
    newestLoadedSequenceNumber?: number | null;
  };
};

export type WhatsAppSessionItem = {
  numberId: string;
  phoneNumber: string;
  jid: string;
  startedAt: string;
  state: 'CONNECTING' | 'CONNECTED' | 'DISCONNECTED';
  hasQrCode: boolean;
};

export type WhatsAppNumberItem = {
  id: string;
  ownerUserId: string;
  ownerName: string;
  ownerEmail?: string | null;
  phoneNumber: string;
  jid: string;
  label?: string | null;
  sessionKey: string;
  status: 'ACTIVE' | 'PAUSED' | 'DISCONNECTED' | 'NEEDS_RELINK';
  automation?: {
    transferAfterMinutes?: number | null;
    transferToUserId?: string | null;
    notifyAfterMinutes?: number | null;
    notifyUserId?: string | null;
  } | null;
  connection?: string | null;
  reconnectScheduled?: boolean | null;
  reconnectAttempt?: number | null;
  maxAutoReconnectAttempts?: number | null;
  nextRetryAt?: string | null;
  pairingError?: string | null;
  pairingStatusCode?: number | null;
  disconnectedAt?: string | null;
  lastConnectedAt?: string | null;
  lastSeenAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type WhatsAppOwnerItem = {
  id: string;
  name: string;
  email?: string | null;
  stats?: {
    total: number;
    needsReply: number;
    overdue: number;
  };
};

export type WhatsAppMediaOperatorRow = {
  ownerUserId: string;
  ownerName: string;
  numberId: string;
  phoneNumber: string;
  label: string | null;
  total: number;
  completed: number;
  failed: number;
  pending: number;
  processing: number;
};

export type WhatsAppMediaProcessingStatus = {
  pending: number;
  failed: number;
  inFlight: number;
  completed?: number;
  processing?: number;
  byOperator?: WhatsAppMediaOperatorRow[];
};

export type WhatsAppNumberHealth = {
  numberId: string;
  phoneNumber?: string | null;
  label?: string | null;
  status: string;
  connection?: string | null;
  reconnectScheduled?: boolean;
  reconnectAttempt?: number | null;
  maxAutoReconnectAttempts?: number | null;
  nextRetryAt?: string | null;
  pairingError?: string | null;
  lastConnectedAt?: string | null;
  lastSeenAt?: string | null;
  disconnectedAt?: string | null;
  hasQrCode?: boolean;
  startedAt?: string | null;
  media?: {
    pending: number;
    failed: number;
    inFlight: number;
    queuePending: number;
    queueFailed: number;
    completed: number;
    processing: number;
  };
};

export type WhatsAppSearchHit = {
  id: string;
  conversationId?: string;
  externalMessageId?: string | null;
  threadId: string;
  chatType?: 'DIRECT' | 'GROUP' | string | null;
  groupJid?: string | null;
  groupName?: string | null;
  direction: 'INBOUND' | 'OUTBOUND';
  messageType?: string | null;
  sequenceNumber?: number | null;
  occurredAt?: string | null;
  sentAt?: string | null;
  customerId?: string | null;
  maosCustomerName?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  customerJid?: string | null;
  displayName?: string | null;
  displayPhone?: string | null;
  identityKind?: string | null;
  senderName?: string | null;
  senderJid?: string | null;
  groupParticipantJid?: string | null;
  participantMap?: Record<string, {
    jid?: string | null;
    phone?: string | null;
    displayName?: string | null;
    displayPhone?: string | null;
    kind?: string | null;
  }> | null;
  threadStatus?: WhatsAppThreadStatus | null;
  salespersonUserId?: string | null;
  salespersonName?: string | null;
  ownerName?: string | null;
  assignedUserName?: string | null;
  numberLabel?: string | null;
  mediaKind?: 'IMAGE' | 'DOCUMENT' | 'AUDIO' | 'VIDEO' | null;
  mediaFileName?: string | null;
  hasTranscript?: boolean;
  transcriptStatus?: 'NOT_REQUESTED' | 'PENDING' | 'COMPLETED' | 'FAILED' | null;
  transcriptText?: string | null;
  extractedText?: string | null;
  snippet?: string | null;
  displaySnippet?: string | null;
};

export type WhatsAppTravelPriceHint = {
  fromCode: string;
  toCode: string;
  departureDate: string | null;
  passengerCount: number | null;
  seatPriceUSD: number;
  totalPriceUSD: number | null;
  currencyCode: string;
  source: 'OVERRIDE' | 'ROUTE_BANK' | 'POLICY_DEFAULT';
  routeId?: string | null;
} | null;

export type WhatsAppThreadNote = {
  id: string;
  threadId: string;
  userId: string;
  body: string;
  createdAt: string;
  updatedAt: string;
  Author?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
};

export type WhatsAppOgPreview = {
  ok: boolean;
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
  domain?: string;
  url: string;
};

export type WhatsAppSendLinkPreview = {
  url: string;
  title?: string | null;
  description?: string | null;
  image?: string | null;
  domain?: string | null;
};

const ogPreviewMemoryCache = new Map<string, WhatsAppOgPreview | null>();

const asObject = (value: any): Record<string, any> => (value && typeof value === 'object' ? value : {});
const toArray = <T = any>(value: any): T[] => (Array.isArray(value) ? value : []);
const firstString = (...values: any[]): string => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const next = String(value).trim();
    if (next) return next;
  }
  return '';
};
const firstNullableString = (...values: any[]): string | null => {
  const value = firstString(...values);
  return value || null;
};
const toBoolean = (value: any, fallback = false): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return fallback;
};
const toNumberOr = (value: any, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const normalizeStatus = (value: any): WhatsAppThreadStatus => {
  const normalized = String(value || '').trim().toUpperCase();
  if (['OPEN', 'WAITING_CLIENT', 'RESOLVED_WON', 'RESOLVED_LOST'].includes(normalized)) {
    return normalized as WhatsAppThreadStatus;
  }
  return 'OPEN';
};
const normalizeQueueItem = (raw: any): WhatsAppQueueItem => {
  const item = asObject(raw);
  const threadId = firstString(item.threadId, item.conversationId, item.id);
  return {
    id: firstString(item.id, threadId),
    conversationId: firstNullableString(item.conversationId, threadId) || undefined,
    numberId: firstString(item.numberId),
    numberLabel: firstNullableString(item.numberLabel),
    numberPhone: firstNullableString(item.numberPhone),
    ownerUserId: firstNullableString(item.ownerUserId),
    ownerName: firstNullableString(item.ownerName),
    customerId: firstNullableString(item.customerId),
    maosCustomerName: firstNullableString(item.maosCustomerName),
    customerJid: firstString(item.customerJid),
    displayName: firstNullableString(item.displayName),
    displayPhone: firstNullableString(item.displayPhone),
    identityKind: firstNullableString(item.identityKind),
    participantMap: item.participantMap && typeof item.participantMap === 'object' ? item.participantMap : null,
    chatType: firstNullableString(item.chatType),
    groupJid: firstNullableString(item.groupJid),
    groupName: firstNullableString(item.groupName),
    customerPhone: firstNullableString(item.customerPhone),
    customerName: firstNullableString(item.customerName),
    profilePhotoUrl: firstNullableString(item.profilePhotoUrl),
    salespersonUserId: firstNullableString(item.salespersonUserId),
    salespersonName: firstNullableString(item.salespersonName),
    assignedUserId: firstNullableString(item.assignedUserId),
    assignedUserName: firstNullableString(item.assignedUserName),
    status: normalizeStatus(item.status),
    lastInboundAt: firstNullableString(item.lastInboundAt),
    lastOutboundAt: firstNullableString(item.lastOutboundAt),
    lastMessageAt: firstNullableString(item.lastMessageAt),
    slaDeadlineAt: firstNullableString(item.slaDeadlineAt),
    needsReply: toBoolean(item.needsReply, false),
    isOverdue: toBoolean(item.isOverdue, false),
    escalationLevel: toNumberOr(item.escalationLevel, 0),
    unreadInboundCount: Math.max(0, toNumberOr(item.unreadInboundCount, 0)),
    lastReadAt: firstNullableString(item.lastReadAt),
    lastReadMessageId: firstNullableString(item.lastReadMessageId),
    lastMessage: item.lastMessage
      ? {
          id: firstString(item.lastMessage.id),
          externalMessageId: firstNullableString(item.lastMessage.externalMessageId) || undefined,
          direction: String(item.lastMessage.direction || 'INBOUND').toUpperCase() === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND',
          body: firstNullableString(item.lastMessage.body) || undefined,
          resolvedBody: firstNullableString(item.lastMessage.resolvedBody) || undefined,
          displayBody: firstNullableString(item.lastMessage.displayBody) || undefined,
          messageType: firstNullableString(item.lastMessage.messageType) || undefined,
          occurredAt: firstNullableString(item.lastMessage.occurredAt) || undefined,
          sequenceNumber: Number.isFinite(Number(item.lastMessage.sequenceNumber)) ? Number(item.lastMessage.sequenceNumber) : null,
          createdAt: firstNullableString(item.lastMessage.createdAt) || undefined,
        }
      : null,
  };
};
const normalizeThreadDetail = (raw: any): WhatsAppThreadDetail => {
  const detail = asObject(raw);
  const threadId = firstString(detail.id, detail.threadId, detail.conversationId);
  const messages = toArray<any>(detail.Messages || detail.messages).map((message: any) => {
    const normalizedType = String(message?.messageType || '').toUpperCase();
    const mediaAsset = message?.MediaAsset || message?.mediaAsset || null;
    const hasStoredMedia =
      !!String(message?.mediaUrl || '').trim() ||
      !!String(mediaAsset?.storagePath || mediaAsset?.storageKey || '').trim() ||
      !!String(message?.rawPayload?.mediaStoragePath || message?.rawPayload?.mediaStorageKey || '').trim();
    const mediaUrl = withApiBase(
      message?.mediaUrl ||
      (
        hasStoredMedia && ['IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT'].includes(normalizedType)
          ? `/api/whatsapp/media/${encodeURIComponent(String(message?.externalMessageId || message?.id || ''))}`
          : null
      )
    );
    return {
      ...message,
      mediaUrl,
      MediaAsset: mediaAsset,
    };
  });
  return {
    ...(detail as any),
    id: threadId,
    conversationId: firstString(detail.conversationId, threadId),
    profilePhotoUrl: firstNullableString(detail.profilePhotoUrl),
    displayName: firstNullableString(detail.displayName),
    displayPhone: firstNullableString(detail.displayPhone),
    identityKind: firstNullableString(detail.identityKind),
    participantMap: detail.participantMap && typeof detail.participantMap === 'object' ? detail.participantMap : null,
    status: normalizeStatus(detail.status),
    Messages: messages,
    Requests: toArray<any>(detail.Requests || detail.requests),
    Alerts: toArray<any>(detail.Alerts || detail.alerts),
    Notes: toArray<any>(detail.Notes || detail.notes),
    messagePage: detail.messagePage && typeof detail.messagePage === 'object'
      ? {
          limit: toNumberOr(detail.messagePage.limit, messages.length || 0),
          beforeSequenceNumber: Number.isFinite(Number(detail.messagePage.beforeSequenceNumber)) ? Number(detail.messagePage.beforeSequenceNumber) : null,
          hasMoreMessagesBefore: toBoolean(detail.messagePage.hasMoreMessagesBefore, false),
          nextBeforeSequenceNumber: Number.isFinite(Number(detail.messagePage.nextBeforeSequenceNumber)) ? Number(detail.messagePage.nextBeforeSequenceNumber) : null,
          oldestLoadedSequenceNumber: Number.isFinite(Number(detail.messagePage.oldestLoadedSequenceNumber)) ? Number(detail.messagePage.oldestLoadedSequenceNumber) : null,
          newestLoadedSequenceNumber: Number.isFinite(Number(detail.messagePage.newestLoadedSequenceNumber)) ? Number(detail.messagePage.newestLoadedSequenceNumber) : null,
        }
      : undefined,
  } as WhatsAppThreadDetail;
};
const normalizeSearchHit = (raw: any): WhatsAppSearchHit => {
  const item = asObject(raw);
  const threadId = firstString(item.threadId, item.conversationId);
  return {
    id: firstString(item.id),
    conversationId: firstNullableString(item.conversationId, threadId) || undefined,
    externalMessageId: firstNullableString(item.externalMessageId),
    threadId,
    chatType: firstNullableString(item.chatType),
    groupJid: firstNullableString(item.groupJid),
    groupName: firstNullableString(item.groupName),
    direction: String(item.direction || 'INBOUND').toUpperCase() === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND',
    messageType: firstNullableString(item.messageType),
    sequenceNumber: Number.isFinite(Number(item.sequenceNumber)) ? Number(item.sequenceNumber) : null,
    occurredAt: firstNullableString(item.occurredAt),
    sentAt: firstNullableString(item.sentAt),
    customerId: firstNullableString(item.customerId),
    maosCustomerName: firstNullableString(item.maosCustomerName),
    customerName: firstNullableString(item.customerName),
    customerPhone: firstNullableString(item.customerPhone),
    customerJid: firstNullableString(item.customerJid),
    displayName: firstNullableString(item.displayName),
    displayPhone: firstNullableString(item.displayPhone),
    identityKind: firstNullableString(item.identityKind),
    senderName: firstNullableString(item.senderName),
    senderJid: firstNullableString(item.senderJid),
    groupParticipantJid: firstNullableString(item.groupParticipantJid),
    participantMap: item.participantMap && typeof item.participantMap === 'object' ? item.participantMap : null,
    threadStatus: firstNullableString(item.threadStatus) as WhatsAppThreadStatus | null,
    salespersonUserId: firstNullableString(item.salespersonUserId),
    salespersonName: firstNullableString(item.salespersonName),
    ownerName: firstNullableString(item.ownerName),
    assignedUserName: firstNullableString(item.assignedUserName),
    numberLabel: firstNullableString(item.numberLabel),
    mediaKind: firstNullableString(item.mediaKind) as 'IMAGE' | 'DOCUMENT' | 'AUDIO' | 'VIDEO' | null,
    mediaFileName: firstNullableString(item.mediaFileName),
    hasTranscript: toBoolean(item.hasTranscript, false),
    transcriptStatus: firstNullableString(item.transcriptStatus) as 'NOT_REQUESTED' | 'PENDING' | 'COMPLETED' | 'FAILED' | null,
    transcriptText: firstNullableString(item.transcriptText),
    extractedText: firstNullableString(item.extractedText),
    snippet: firstNullableString(item.snippet),
    displaySnippet: firstNullableString(item.displaySnippet),
  };
};

export const whatsappService = {
  async getQueue(params?: {
    numberId?: string;
    ownerUserId?: string;
    assignedUserId?: string;
    assignedToMe?: boolean;
    needsReply?: boolean;
    overdueOnly?: boolean;
    includeUnreadCounts?: boolean;
    status?: string;
    q?: string;
    limit?: number;
    page?: number;
  }): Promise<WhatsAppQueueResponse> {
    const normalizedKey = normalizeQueueBrokerKey(params);
    const now = Date.now();
    const existing = whatsAppQueueBroker.get(normalizedKey);
    if (existing && now - existing.at < WHATSAPP_QUEUE_GLOBAL_DEDUPE_MS) {
      return existing.promise;
    }
    if (isWhatsAppDebugEnabled()) {
      console.debug('[WA_OBS][QUEUE_CALLSITE]', {
        normalizedKey,
        stack: new Error().stack?.split('\n').slice(0, 8),
      });
    }
    const requestPromise = api.get('/whatsapp/queue', freshConfig(params)).then((response) => {
    const data = response.data?.data || {};
    return {
      items: toArray<any>(data.items).map(normalizeQueueItem),
      total: toNumberOr(data.total, 0),
      page: toNumberOr(data.page, 1),
      limit: toNumberOr(data.limit, 25),
      totalPages: toNumberOr(data.totalPages, 1),
      unreadInboundTotal: Math.max(0, toNumberOr(data.unreadInboundTotal, 0)),
    };
    }).finally(() => {
      const current = whatsAppQueueBroker.get(normalizedKey);
      if (current?.promise === requestPromise) {
        window.setTimeout(() => {
          const latest = whatsAppQueueBroker.get(normalizedKey);
          if (latest?.promise === requestPromise) {
            whatsAppQueueBroker.delete(normalizedKey);
          }
        }, WHATSAPP_QUEUE_GLOBAL_DEDUPE_MS);
      }
    });
    whatsAppQueueBroker.set(normalizedKey, { at: now, promise: requestPromise });
    return requestPromise;
  },

  async forwardMessage(
    messageId: string,
    payload: {
      targetThreadId?: string;
      targetThreadIds?: string[];
      caption?: string | null;
      captionMode?: 'KEEP' | 'REMOVE' | 'SET';
    },
  ): Promise<{
    threadId: string;
    messageId: string;
    sentAt?: string | null;
    forwarded?: Array<{ threadId: string; messageId: string; sentAt?: string | null }>;
    failed?: Array<{ threadId: string; error: string }>;
    requestedCount?: number;
    forwardedCount?: number;
    failedCount?: number;
  }> {
    const response = await api.post(`/whatsapp/message/${encodeURIComponent(messageId)}/forward`, payload);
    return response.data?.data;
  },

  async getThreadDetail(
    threadId: string,
    params?: { messageLimit?: number; beforeSequenceNumber?: number | null }
  ): Promise<WhatsAppThreadDetail> {
    const normalizedKey = normalizeThreadDetailBrokerKey(threadId, params);
    const now = Date.now();
    const existing = whatsAppThreadDetailBroker.get(normalizedKey);
    if (existing && now - existing.at < WHATSAPP_THREAD_DETAIL_GLOBAL_DEDUPE_MS) {
      return existing.promise;
    }
    if (isWhatsAppDebugEnabled()) {
      console.debug('[WA_OBS][THREAD_DETAIL_CALLSITE]', {
        normalizedKey,
        stack: new Error().stack?.split('\n').slice(0, 8),
      });
    }
    const requestPromise = api
      .get(`/whatsapp/thread/${encodeURIComponent(threadId)}`, freshConfig(params))
      .then((response) => normalizeThreadDetail(response.data?.data))
      .finally(() => {
        const current = whatsAppThreadDetailBroker.get(normalizedKey);
        if (current?.promise === requestPromise) {
          window.setTimeout(() => {
            const latest = whatsAppThreadDetailBroker.get(normalizedKey);
            if (latest?.promise === requestPromise) {
              whatsAppThreadDetailBroker.delete(normalizedKey);
            }
          }, WHATSAPP_THREAD_DETAIL_GLOBAL_DEDUPE_MS);
        }
      });
    whatsAppThreadDetailBroker.set(normalizedKey, { at: now, promise: requestPromise });
    return requestPromise;
  },

  getMediaDownloadUrl(messageId: string): string {
    const raw = `${API_BASE}/whatsapp/media/${encodeURIComponent(messageId)}/download`;
    const token = getJwtToken();
    return token ? `${raw}?token=${encodeURIComponent(token)}` : raw;
  },

  /** Inline URL — served with Content-Disposition: inline so browsers render PDF/images rather than downloading */
  getMediaInlineUrl(messageId: string): string {
    const raw = `${API_BASE}/whatsapp/media/${encodeURIComponent(messageId)}`;
    const token = getJwtToken();
    return token ? `${raw}?token=${encodeURIComponent(token)}` : raw;
  },

  async requestTranscript(messageId: string): Promise<{ messageId: string; transcriptStatus: 'NOT_REQUESTED' | 'PENDING' | 'COMPLETED' | 'FAILED'; transcriptText?: string | null; transcriptGeneratedAt?: string | null }> {
    const response = await api.post(`/whatsapp/message/${encodeURIComponent(messageId)}/transcript`);
    return response.data?.data;
  },

  async retryMediaRecovery(
    messageId: string,
  ): Promise<{ messageId: string; recovered: boolean; mediaUrl?: string | null; error?: string | null }> {
    const response = await api.post(`/whatsapp/message/${encodeURIComponent(messageId)}/media/retry`);
    return response.data?.data;
  },

  async getTravelPriceHint(payload: {
    fromCode: string;
    toCode: string;
    departureDate?: string | null;
    passengerCount?: number | null;
  }): Promise<WhatsAppTravelPriceHint> {
    const response = await api.post('/whatsapp/assistant/travel-price-hint', payload);
    return response.data?.data || null;
  },

  async assignThread(threadId: string, assignedUserId?: string, note?: string | null): Promise<{ threadId: string; assignedUserId: string; assignedUserName: string; assignedUserEmail?: string | null }> {
    const response = await api.patch(`/whatsapp/assign/${encodeURIComponent(threadId)}`, {
      assignedUserId,
      note: String(note || '').trim() || null,
    });
    return response.data?.data;
  },

  async sendMessage(
    threadId: string,
    text: string,
    replyToMessageId?: string | null,
    mentionedJids?: string[] | null,
    linkPreview?: WhatsAppSendLinkPreview | null,
    clientRequestId?: string | null,
  ): Promise<{ threadId: string; messageId: string; sentAt?: string | null }> {
    const response = await api.post(`/whatsapp/thread/${encodeURIComponent(threadId)}/send`, {
      text,
      replyToMessageId: replyToMessageId || null,
      mentionedJids: Array.isArray(mentionedJids) ? mentionedJids : [],
      linkPreview: linkPreview || null,
      clientRequestId: String(clientRequestId || '').trim() || null,
    });
    return response.data?.data;
  },

  async reactToMessage(
    messageId: string,
    payload: { emoji: string; threadId?: string | null }
  ): Promise<{ threadId: string; messageId: string; externalMessageId?: string | null; emoji: string }> {
    const response = await api.post(`/whatsapp/message/${encodeURIComponent(messageId)}/react`, {
      emoji: String(payload.emoji || '').trim(),
      threadId: String(payload.threadId || '').trim() || null,
    });
    return response.data?.data;
  },

  async recallMessage(
    messageId: string,
    payload?: { threadId?: string | null; reason?: string | null }
  ): Promise<{ threadId: string; messageId: string; externalMessageId?: string | null; recalledAt?: string | null }> {
    const response = await api.post(`/whatsapp/message/${encodeURIComponent(messageId)}/recall`, {
      threadId: String(payload?.threadId || '').trim() || null,
      reason: String(payload?.reason || '').trim() || null,
    });
    return response.data?.data;
  },

  async editMessage(
    threadId: string,
    messageId: string,
    payload: { text: string; clientRequestId?: string | null },
  ): Promise<{ threadId: string; messageId: string; externalMessageId?: string | null; editedAt?: string | null }> {
    const response = await api.patch(
      `/whatsapp/thread/${encodeURIComponent(threadId)}/message/${encodeURIComponent(messageId)}/edit`,
      {
        text: String(payload.text || '').trim(),
        clientRequestId: String(payload.clientRequestId || '').trim() || null,
      },
    );
    return response.data?.data;
  },

  async sendAttachment(
    threadId: string,
    payload: { base64Data: string; mimeType: string; fileName?: string; caption?: string; replyToMessageId?: string | null; mentionedJids?: string[] | null }
  ): Promise<{ threadId: string; messageId: string; sentAt?: string | null }> {
    const response = await api.post(`/whatsapp/thread/${encodeURIComponent(threadId)}/send`, {
      text: payload.caption || '',
      replyToMessageId: payload.replyToMessageId || null,
      mentionedJids: Array.isArray(payload.mentionedJids) ? payload.mentionedJids : [],
      attachment: payload,
    });
    return response.data?.data;
  },

  async updateThread(threadId: string, data: { customerId?: string | null }): Promise<void> {
    await api.patch(`/whatsapp/thread/${encodeURIComponent(threadId)}`, data);
  },

  async updateThreadStatus(threadId: string, status: WhatsAppThreadStatus, reason?: string | null): Promise<WhatsAppThreadDetail> {
    const response = await api.patch(`/whatsapp/thread/${encodeURIComponent(threadId)}/status`, { status, reason: reason || null });
    return normalizeThreadDetail(response.data?.data);
  },

  async unassignThread(threadId: string, reason?: string | null): Promise<{ threadId: string; assignedUserId: null }> {
    const response = await api.patch(`/whatsapp/thread/${encodeURIComponent(threadId)}/unassign`, { reason: reason || null });
    return response.data?.data;
  },

  async markThreadRead(
    threadId: string,
    payload?: { lastReadAt?: string; lastReadMessageId?: string | null }
  ): Promise<WhatsAppThreadDetail> {
    const response = await api.patch(`/whatsapp/thread/${encodeURIComponent(threadId)}/read`, payload || {});
    return normalizeThreadDetail(response.data?.data);
  },

  async bulkAction(payload: { action: 'ASSIGN_TO_ME' | 'ASSIGN_TO_USER' | 'WAITING_CLIENT'; threadIds: string[]; assignedUserId?: string; note?: string | null }): Promise<{ action: string; updatedCount: number; threadIds: string[] }> {
    const response = await api.post('/whatsapp/queue/bulk', payload);
    return response.data?.data;
  },

  async getSessions(): Promise<{ items: WhatsAppSessionItem[] }> {
    const response = await api.get('/whatsapp/sessions');
    return response.data?.data || { items: [] };
  },

  async listNumbers(): Promise<{ items: WhatsAppNumberItem[] }> {
    const response = await api.get('/whatsapp/numbers');
    return response.data?.data || { items: [] };
  },

  async listOwners(): Promise<{ items: WhatsAppOwnerItem[] }> {
    const response = await api.get('/whatsapp/owners');
    return response.data?.data || { items: [] };
  },

  async getMediaProcessingStatus(): Promise<WhatsAppMediaProcessingStatus> {
    const response = await api.get('/whatsapp/media/processing/status');
    return response.data?.data || { pending: 0, failed: 0, inFlight: 0 };
  },

  async getNumberHealth(numberId: string): Promise<WhatsAppNumberHealth> {
    const response = await api.get(`/whatsapp/numbers/${encodeURIComponent(numberId)}/health`);
    const data = response.data?.data || {};
    return {
      numberId: firstString(data.numberId, numberId),
      phoneNumber: firstNullableString(data.phoneNumber),
      label: firstNullableString(data.label),
      status: String(data.status || 'UNKNOWN').trim().toUpperCase(),
      connection: firstNullableString(data.connection),
      reconnectScheduled: toBoolean(data.reconnectScheduled, false),
      reconnectAttempt: Number.isFinite(Number(data.reconnectAttempt)) ? Number(data.reconnectAttempt) : null,
      maxAutoReconnectAttempts: Number.isFinite(Number(data.maxAutoReconnectAttempts))
        ? Number(data.maxAutoReconnectAttempts)
        : null,
      nextRetryAt: firstNullableString(data.nextRetryAt),
      pairingError: firstNullableString(data.pairingError),
      lastConnectedAt: firstNullableString(data.lastConnectedAt),
      lastSeenAt: firstNullableString(data.lastSeenAt),
      disconnectedAt: firstNullableString(data.disconnectedAt),
      hasQrCode: toBoolean(data.hasQrCode, false),
      startedAt: firstNullableString(data.startedAt),
      media: {
        pending: Math.max(0, toNumberOr(data.media?.pending, 0)),
        failed: Math.max(0, toNumberOr(data.media?.failed, 0)),
        inFlight: Math.max(0, toNumberOr(data.media?.inFlight, 0)),
        queuePending: Math.max(0, toNumberOr(data.media?.queuePending, 0)),
        queueFailed: Math.max(0, toNumberOr(data.media?.queueFailed, 0)),
        completed: Math.max(0, toNumberOr(data.media?.completed, 0)),
        processing: Math.max(0, toNumberOr(data.media?.processing, 0)),
      },
    };
  },

  async searchMessages(params: {
    q: string;
    ownerUserId?: string;
    assignedUserId?: string;
    limit?: number;
    scope?: 'ALL' | 'MESSAGES' | 'MEDIA' | 'DOCUMENTS';
  }): Promise<{ items: WhatsAppSearchHit[] }> {
    const response = await api.get('/whatsapp/search', freshConfig(params));
    const data = response.data?.data || {};
    return {
      items: toArray<any>(data.items).map(normalizeSearchHit),
    };
  },

  async getOgPreview(url: string, options?: { signal?: AbortSignal }): Promise<WhatsAppOgPreview | null> {
    const rawUrl = String(url || '').trim();
    if (!rawUrl) return null;
    const response = await api.get('/whatsapp/og-preview', {
      params: { url: rawUrl },
      signal: options?.signal,
    });
    const preview = response.data || null;
    ogPreviewMemoryCache.set(rawUrl, preview);
    return preview;
  },

  getCachedSendLinkPreview(url: string): WhatsAppSendLinkPreview | null {
    const rawUrl = String(url || '').trim();
    if (!rawUrl) return null;
    const preview = ogPreviewMemoryCache.get(rawUrl);
    if (!preview?.ok || (!preview?.title && !preview?.domain && !preview?.image)) return null;
    return {
      url: rawUrl,
      title: preview.title || preview.domain || null,
      description: preview.description || null,
      image: preview.image || null,
      domain: preview.domain || null,
    };
  },

  async backfillMediaProcessing(payload?: { limit?: number; includeFailed?: boolean; runAll?: boolean }): Promise<WhatsAppMediaProcessingStatus & { requested?: number; processed?: number; includeFailed?: boolean; backgroundJob?: boolean; progress?: any }> {
    const response = await api.post('/whatsapp/media/backfill', payload || {});
    return response.data?.data;
  },

  async getBackfillProgress(): Promise<WhatsAppMediaProcessingStatus & { progress: { running: boolean; processed: number; total: number; startedAt: string | null; finishedAt: string | null } }> {
    const response = await api.get('/whatsapp/media/backfill/progress');
    return response.data?.data;
  },

  async createNumber(payload: {
    ownerUserId: string;
    phoneNumber: string;
    jid: string;
    label?: string;
    sessionKey: string;
    status?: 'ACTIVE' | 'PAUSED' | 'DISCONNECTED' | 'NEEDS_RELINK';
    automation?: {
      transferAfterMinutes?: number | null;
      transferToUserId?: string | null;
      notifyAfterMinutes?: number | null;
      notifyUserId?: string | null;
    } | null;
  }): Promise<WhatsAppNumberItem> {
    const response = await api.post('/whatsapp/numbers', payload);
    return response.data?.data;
  },

  async updateNumber(id: string, payload: {
    ownerUserId: string;
    phoneNumber: string;
    jid: string;
    label?: string;
    sessionKey: string;
    status?: 'ACTIVE' | 'PAUSED' | 'DISCONNECTED' | 'NEEDS_RELINK';
    automation?: {
      transferAfterMinutes?: number | null;
      transferToUserId?: string | null;
      notifyAfterMinutes?: number | null;
      notifyUserId?: string | null;
    } | null;
  }): Promise<WhatsAppNumberItem> {
    const response = await api.patch(`/whatsapp/numbers/${encodeURIComponent(id)}`, payload);
    return response.data?.data;
  },

  async connectNumber(numberId: string, options?: { forceRelink?: boolean }): Promise<{ numberId: string; state: string; hasQrCode: boolean }> {
    const response = await api.post(`/whatsapp/numbers/${encodeURIComponent(numberId)}/connect`, {
      forceRelink: !!options?.forceRelink,
    });
    return response.data?.data;
  },

  async disconnectNumber(numberId: string): Promise<{ numberId: string; state: string }> {
    const response = await api.post(`/whatsapp/numbers/${encodeURIComponent(numberId)}/disconnect`);
    return response.data?.data;
  },

  async deactivateNumber(numberId: string): Promise<WhatsAppNumberItem> {
    const response = await api.post(`/whatsapp/numbers/${encodeURIComponent(numberId)}/deactivate`);
    return response.data?.data;
  },

  async deleteNumber(numberId: string): Promise<{ id: string; phoneNumber: string; label?: string | null; deleted: boolean }> {
    const response = await api.delete(`/whatsapp/numbers/${encodeURIComponent(numberId)}`);
    return response.data?.data;
  },

  async getQrCode(numberId: string): Promise<{ numberId: string; qrCode?: string | null; hasQrCode: boolean }> {
    const response = await api.get(`/whatsapp/numbers/${encodeURIComponent(numberId)}/qr`);
    return response.data?.data;
  },

  async getStatsReport(): Promise<WhatsAppStatsReport> {
    const response = await api.get('/whatsapp/stats/report', freshConfig());
    return response.data?.data;
  },

  async createThreadNote(threadId: string, body: string): Promise<WhatsAppThreadNote> {
    const response = await api.post(`/whatsapp/thread/${encodeURIComponent(threadId)}/notes`, { body });
    return response.data?.data;
  },

  async deleteThreadNote(threadId: string, noteId: string): Promise<void> {
    await api.delete(`/whatsapp/thread/${encodeURIComponent(threadId)}/notes/${encodeURIComponent(noteId)}`);
  },
};
